import { useEffect } from "react";
import { useNavigate } from "react-router";
import { getCalibrationMeasurementSet } from "../../API/calibration";
import {
  isStalled,
  TrackedCalibration,
  useTrackedCalibrations,
} from "./tracked-calibrations";

const INITIAL_POLL_DELAY_MS = 5_000;
const MAX_POLL_DELAY_MS = 30_000;
export const MAX_POLL_FAILURES = 5;

export function useCalibrationPolling({
  id,
  sourceResultSetId,
  status,
}: TrackedCalibration) {
  const navigate = useNavigate();
  const polling = status === "in_progress";

  useEffect(() => {
    if (!polling) return;
    const { update, setStatus } = useTrackedCalibrations.getState();

    let cancelled = false;
    let inFlight = false;
    let failures = 0;
    let delay = INITIAL_POLL_DELAY_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      clearTimeout(timer);
      if (inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const measurementSet = await getCalibrationMeasurementSet(id);
        if (cancelled) return;
        failures = 0;
        update(measurementSet);
        if (measurementSet.status !== "in_progress") return;
        if (isStalled(measurementSet.created_at)) {
          setStatus(sourceResultSetId, id, "stalled");
          return;
        }
      } catch (error) {
        if (cancelled) return;
        if (error instanceof Response) {
          void navigate("/login");
          return;
        }
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          setStatus(sourceResultSetId, id, "unknown");
          return;
        }
      } finally {
        inFlight = false;
      }
      timer = setTimeout(check, delay + Math.random() * 1000);
      delay = Math.min(delay * 1.5, MAX_POLL_DELAY_MS);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    void check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [id, sourceResultSetId, polling, navigate]);
}
